/**
 * Shared, forgiving text normalization for owner and address search.
 *
 * The county search is a client-side substring match over Arrow data. Users
 * routinely paste a full postal address ("1100 Marlin Ct, Waukesha, WI 53186")
 * or type the spelled-out street suffix ("Marlin Court"), and the naive
 * "split on whitespace, every token must appear" match drops them to zero
 * results because:
 *   - punctuation stays glued to tokens ("CT," never matches "CT")
 *   - the state token ("WI") and the ZIP live outside the address string
 *   - the data stores abbreviated suffixes ("CT") but the user typed "COURT"
 *
 * These helpers canonicalize BOTH the query and the parcel haystack the same
 * way (uppercase, strip punctuation, fold USPS suffix/directional synonyms,
 * drop state stopwords) so the two sides line up. Matching stays substring-
 * based per token, so partial/type-ahead queries keep working and recall is a
 * strict superset of the old behavior.
 */

// USPS street-suffix synonyms folded to a single canonical token. Both the
// spelled-out word and the abbreviation map to the same value so "COURT" and
// "CT" are interchangeable on either side of the match.
const SUFFIX_CANON: Record<string, string> = {
  STREET: "ST", ST: "ST",
  AVENUE: "AVE", AVE: "AVE", AV: "AVE",
  ROAD: "RD", RD: "RD",
  DRIVE: "DR", DR: "DR",
  LANE: "LN", LN: "LN",
  COURT: "CT", CT: "CT",
  BOULEVARD: "BLVD", BLVD: "BLVD",
  PLACE: "PL", PL: "PL",
  TERRACE: "TER", TER: "TER", TERR: "TER",
  CIRCLE: "CIR", CIR: "CIR",
  TRAIL: "TRL", TRL: "TRL",
  HIGHWAY: "HWY", HWY: "HWY",
  PARKWAY: "PKWY", PKWY: "PKWY",
  SQUARE: "SQ", SQ: "SQ",
  POINT: "PT", PT: "PT",
  RIDGE: "RDG", RDG: "RDG",
  HEIGHTS: "HTS", HTS: "HTS",
  CROSSING: "XING", XING: "XING",
  BEND: "BND", BND: "BND",
  COVE: "CV", CV: "CV",
};

const DIRECTIONAL: Record<string, string> = {
  NORTH: "N", N: "N",
  SOUTH: "S", S: "S",
  EAST: "E", E: "E",
  WEST: "W", W: "W",
  NORTHEAST: "NE", NE: "NE",
  NORTHWEST: "NW", NW: "NW",
  SOUTHEAST: "SE", SE: "SE",
  SOUTHWEST: "SW", SW: "SW",
};

// Tokens that appear in a pasted postal address but never in our parcel
// address/place fields, so keeping them would force a false miss.
const ADDRESS_STOPWORDS = new Set(["WI", "WISC", "WISCONSIN", "USA"]);

function baseTokens(text: string): string[] {
  return text
    .toUpperCase()
    .replace(/['`.]/g, "") // drop apostrophes/periods: O'BRIEN -> OBRIEN, JR. -> JR
    .replace(/[^A-Z0-9 ]/g, " ") // every other punctuation becomes a separator
    .split(/\s+/)
    .filter(Boolean);
}

/**
 * Tokenize an address query or parcel address haystack into canonical tokens.
 * State/ZIP stopwords are dropped and USPS suffix + directional synonyms are
 * folded so "Marlin Court" and "MARLIN CT" produce the same tokens.
 */
export function normalizeAddressTokens(text: string | null | undefined): string[] {
  if (!text) return [];
  const out: string[] = [];
  for (const t of foldRoutes(foldPoBox(baseTokens(text)))) {
    if (ADDRESS_STOPWORDS.has(t)) continue;
    out.push(SUFFIX_CANON[t] ?? DIRECTIONAL[t] ?? t);
  }
  return out;
}

/**
 * Every spelling of a post office box, folded to `PO BOX`. Measured over the
 * statewide mailing addresses (2026-10-06): PO BOX 178,844, P O BOX 7,156,
 * POBOX 54, POB 51, PO B 9, PO BX 9, POST OFFICE BOX 2, P BOX 1. Without it
 * "PO BOX 1027" and "P O BOX 1027" — one owner's two LLCs, in Wood County —
 * never matched. The short forms fold only before a number, since "POB" or
 * "P BOX" alone could be anything. A bare "BOX 12" is left alone: it is
 * usually a rural-route box, which is a different mailbox.
 * `PO_BOX_SPELLINGS` is the same list for the SQL side, which can only
 * expand the query.
 */
export const PO_BOX_SPELLINGS = ["PO BOX", "P O BOX", "POBOX", "POB", "PO B", "PO BX", "POST OFFICE BOX", "P BOX"];

function foldPoBox(tokens: string[]): string[] {
  const out: string[] = [];
  const numberAt = (i: number) => i < tokens.length && /^\d/.test(tokens[i]);
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    const next = tokens[i + 1];
    if (t === "P" && next === "O" && tokens[i + 2] === "BOX") {
      out.push("PO", "BOX");
      i += 2;
    } else if (t === "POST" && next === "OFFICE" && tokens[i + 2] === "BOX") {
      out.push("PO", "BOX");
      i += 2;
    } else if (/^POBOX\d*$/.test(t)) {
      out.push("PO", "BOX");
      if (t.length > 5) out.push(t.slice(5));
    } else if (t === "POB" && numberAt(i + 1)) {
      out.push("PO", "BOX");
    } else if (t === "PO" && (next === "B" || next === "BX") && numberAt(i + 2)) {
      out.push("PO", "BOX");
      i += 1;
    } else if (t === "P" && next === "BOX" && numberAt(i + 2)) {
      out.push("PO", "BOX");
      i += 1;
    } else {
      out.push(t);
    }
  }
  return out;
}

/**
 * A numbered or lettered highway, however it is written, folds to `HWY` and
 * its route: "COUNTY ROAD R", "CTH R", "CTY HWY R" and "HWY R" are one road,
 * and so are "STATE ROAD 33", "STH 33", "STATE HWY 33" and "HWY 33".
 *
 * Measured over the statewide site addresses (2026-10-08), a county road is
 * spelled nine ways — COUNTY ROAD 59,964, COUNTY RD 25,989, CTH 18,775,
 * COUNTY HWY 11,047, COUNTY HIGHWAY 8,632, CTY HWY 4,242, CTY 4,013, CO RD
 * 2,088, CTY RD 99 — and a state or US highway nine more. Each county
 * publishes one, and the suffix fold could not bridge them: "11417 COUNTY
 * ROAD R" found nothing on 2026-10-08 because Manitowoc writes "11417 CTH R".
 *
 * The class (county, state, US) is dropped rather than kept: Wisconsin letters
 * its county trunk highways and numbers its state and US ones, so the route
 * already says which, and a bare "HWY 33" has no class to match on anyway.
 * `ROUTE_DESIGNATIONS` is the same list for the SQL side, which can only
 * expand the query.
 */
export const ROUTE_DESIGNATIONS = [
  "COUNTY ROAD", "COUNTY RD", "COUNTY HIGHWAY", "COUNTY HWY", "COUNTY TRUNK HIGHWAY", "COUNTY TRUNK HWY",
  "COUNTY TRUNK", "COUNTY", "CO ROAD", "CO RD", "CO HIGHWAY", "CO HWY", "CTY ROAD", "CTY RD", "CTY HWY",
  "CTY", "CTH", "STATE ROAD", "STATE RD", "STATE HIGHWAY", "STATE HWY", "STATE HY", "STATE TRUNK HIGHWAY",
  "STATE TRUNK HWY", "ST ROAD", "ST RD", "ST HIGHWAY", "ST HWY", "STH", "US HIGHWAY", "US HWY", "US HY",
  "USH", "US", "HIGHWAY", "HWY",
];

const ROAD_WORD = new Set(["ROAD", "RD", "HIGHWAY", "HWY", "HY"]);

/**
 * A route: one or two letters (CTH Q, CTY AA), the same letter three times
 * (CTY MMM) or up to three digits. Not a street suffix or a state, so "STATE
 * ST" and "COUNTY RD" (with no route) stay as they are, and not any other
 * three-letter word, so "COUNTY OAK DR" stays a street.
 */
export function isRouteToken(token: string | undefined): boolean {
  return (
    token !== undefined &&
    /^(\d{1,3}|[A-Z]{1,2}|([A-Z])\2\2)$/.test(token) &&
    !SUFFIX_CANON[token] &&
    !ADDRESS_STOPWORDS.has(token) &&
    token !== "US"
  );
}

function foldRoutes(tokens: string[]): string[] {
  const out: string[] = [];
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    let j = i + 1;
    let designated = false;
    if (t === "COUNTY" || t === "CO" || t === "CTY" || t === "STATE" || t === "ST") {
      if (tokens[j] === "TRUNK") j++;
      const hasRoadWord = ROAD_WORD.has(tokens[j]);
      if (hasRoadWord) j++;
      // A bare "COUNTY K" or "CTY K" is a county road; a bare "STATE" or "ST"
      // or "CO" before a letter is a street name, a saint or a company.
      designated = hasRoadWord || tokens[i + 1] === "TRUNK" || t === "COUNTY" || t === "CTY";
    } else if (t === "US") {
      if (ROAD_WORD.has(tokens[j])) j++;
      // A bare "US" only before a number.
      designated = j > i + 1 || /^\d+$/.test(tokens[j] ?? "");
    } else if (t === "CTH" || t === "STH" || t === "USH" || t === "HWY" || t === "HIGHWAY") {
      designated = true;
    }
    if (designated && isRouteToken(tokens[j])) {
      out.push("HWY", tokens[j]);
      i = j;
    } else {
      out.push(t);
    }
  }
  return out;
}

// A Wisconsin ZIP, bare or ZIP+4 run together: 53177, 531773019.
const WI_ZIP = /^5[34]\d{3}(\d{4})?$/;

// Words that end a street name but have no USPS synonym to fold, plus the
// county/state trunk prefixes a road letter follows ("CTH Q", "CTY AA").
// Only used to find where the street stops; never folded or matched.
const STREET_END = new Set([
  "WAY", "PASS", "CREST", "RUN", "TRACE", "TRCE", "PATH", "VIEW", "VW", "WALK",
  "ROW", "LOOP", "GLEN", "KNOLL", "HOLLOW", "CTH", "CTY", "STH", "CO",
]);

/**
 * Whether tokens[i] can be the last word of a street: a suffix (RD), a
 * directional (N), a route or house number (59, N1234), or a county-road
 * letter right after a suffix or number ("COUNTY RD A", "HWY KR").
 */
function endsStreet(tokens: string[], i: number): boolean {
  const t = tokens[i];
  if (SUFFIX_CANON[t] || DIRECTIONAL[t] || STREET_END.has(t) || /\d/.test(t)) return true;
  const prev = tokens[i - 1];
  return t.length <= 2 && prev !== undefined && (!!SUFFIX_CANON[prev] || STREET_END.has(prev) || /\d/.test(prev));
}

/**
 * Whether tokens[i], walking back from the state/ZIP, is part of the city.
 * Some cities end in a word that is also a street suffix (West BEND, Stevens
 * POINT); a spelled-out one in the last position counts as the city's.
 */
function isCityWord(tokens: string[], i: number, last: boolean): boolean {
  const t = tokens[i];
  if (!/^[A-Z]+$/.test(t)) return false;
  if (last && SUFFIX_CANON[t] && SUFFIX_CANON[t] !== t) return true;
  if (!endsStreet(tokens, i)) return true;
  return t.length > 2 && !!DIRECTIONAL[t] && i >= 1 && endsStreet(tokens, i - 1);
}

/**
 * Tokens for an address the user typed, with any postal tail removed.
 *
 * People paste addresses the way an envelope writes them: "15501 BRAUN RD
 * STURTEVANT WI 531773019". The parcel layer stores only "15501 BRAUN RD", and
 * the postal city is often not the parcel's municipality, so the city and the ZIP+4 turned a one-parcel
 * match into zero. A paying customer did exactly this on 2026-10-02 and only
 * found the parcel after deleting the tail by hand.
 *
 * Only a tail that is recognizably postal is removed: everything after the
 * first comma when the address before it starts with a house number (1100,
 * N1234), or else a
 * trailing state and/or ZIP plus the city words just before them, back to the
 * street suffix, route number or directional that ends the street. Without
 * one of those signals the query is left alone, so "Lake Shore" stays a
 * street name and type-ahead is unchanged.
 *
 * `bareCity` also drops a city typed with no comma, state or ZIP at all
 * ("88705 State Hwy 13 Bayfield"), but only after something that cannot be
 * the middle of a street name: a suffix after a real name word ("MAIN ST"),
 * or a route after its road word ("HWY 13", "COUNTY RD K"). On 2026-10-09
 * that query found nothing statewide and "…Bayfield, WI 54814" found the
 * parcel; the two now search the same thing. It is opt-in because the county
 * address box also receives "Find parcels mailed here", whose city is what
 * keeps one town's 1100 Marlin Ct from matching another's.
 */
export function normalizeAddressQuery(
  text: string | null | undefined,
  { bareCity = false }: { bareCity?: boolean } = {},
): string[] {
  if (!text) return [];
  // Everything after the first comma is postal when a house number leads
  // ("1100 Marlin Ct, Waukesha, WI"). The city can still sit before that
  // comma ("5515 W Washington Blvd Milwaukee, WI 53208"), so the street part
  // goes through the same tail check below.
  let tokens = baseTokens(text);
  let postal = false;
  const comma = text.indexOf(",");
  if (comma > 0) {
    const street = baseTokens(text.slice(0, comma));
    if (street.length >= 2 && /\d/.test(street[0])) {
      tokens = street;
      postal = true;
    }
  }

  let end = tokens.length;
  // Trailing ZIP (bare, ZIP+4, or "53177 3019") and state, never the first token.
  if (end > 2 && /^\d{4}$/.test(tokens[end - 1]) && WI_ZIP.test(tokens[end - 2])) {
    end -= 2;
  } else if (end > 1 && WI_ZIP.test(tokens[end - 1])) {
    end -= 1;
  }
  if (end > 1 && ADDRESS_STOPWORDS.has(tokens[end - 1])) end -= 1;

  if (postal || end < tokens.length) end = cutCity(tokens, end);
  else if (bareCity) end = cutBareCity(tokens, end);
  return normalizeAddressTokens(tokens.slice(0, end).join(" "));
}

// The short words inside Wisconsin place names: Fond du Lac, Prairie du
// Chien, La Crosse, De Pere, Lac du Flambeau. Never a city's last word.
const CITY_PARTICLES = new Set(["DU", "DE", "LA", "LE", "DES"]);

// Road words a route number or letter follows: "HWY 13", "COUNTY RD K",
// "CTH Q". A bare "CO" or "ST" before a letter is a company or a saint.
const ROUTE_LEAD = new Set(["HWY", "HIGHWAY", "HY", "ROAD", "RD", "CTH", "STH", "USH", "COUNTY", "CTY", "US"]);

/**
 * Where the street ends when a city follows it with nothing postal around it.
 * Stricter than `cutCity`, which has a state or ZIP to vouch for the tail:
 * the city is one to four words of three letters or more, or a particle
 * inside one (never a route letter, abbreviated directional or a lone
 * suffix or direction), and the word before it must end
 * a street beyond doubt. A suffix counts only after a name word, so "123 ST
 * JAMES" keeps its saint; a route only after its road word, so "123 N 13TH"
 * is not a route.
 */
function cutBareCity(tokens: string[], end: number): number {
  if (!/\d/.test(tokens[0])) return end;
  const cityWord = (t: string, last: boolean) =>
    (!last && CITY_PARTICLES.has(t)) ||
    (/^[A-Z]{3,}$/.test(t) &&
    !ADDRESS_STOPWORDS.has(t) &&
    !STREET_END.has(t) &&
    !isRouteToken(t) &&
    // "STEVENS POINT", "WEST BEND": a spelled-out suffix ends a city, and a
    // spelled-out direction starts one.
    (!SUFFIX_CANON[t] || (last && SUFFIX_CANON[t] !== t)) &&
    (!DIRECTIONAL[t] || t.length > 2));
  let cut = end;
  while (cut > 2 && end - cut < 4 && cityWord(tokens[cut - 1], cut === end)) cut -= 1;
  if (cut === end) return end;
  // Alone, a suffix or direction is the street's own: "OAK RIDGE COURT",
  // "HWY 51 SOUTH".
  if (end - cut === 1 && (SUFFIX_CANON[tokens[cut]] || DIRECTIONAL[tokens[cut]])) return end;
  const last = tokens[cut - 1];
  const named = tokens
    .slice(1, cut - 1)
    .some((t) => /^[A-Z]+$/.test(t) && !SUFFIX_CANON[t] && !DIRECTIONAL[t]);
  const suffixEnd = (!!SUFFIX_CANON[last] || STREET_END.has(last)) && named;
  const routeEnd = isRouteToken(last) && ROUTE_LEAD.has(tokens[cut - 2]);
  return suffixEnd || routeEnd ? cut : end;
}

/**
 * Where the street ends, given that tokens[0..end) is followed by a postal
 * tail. City words: alphabetic, at most four ("NORTH FOND DU LAC"), back to
 * the token that ends the street. A spelled-out directional right after that
 * token starts a city ("ST WEST BEND"); the street's own is abbreviated
 * ("ST N MILWAUKEE").
 */
function cutCity(tokens: string[], end: number): number {
  let cut = end;
  while (cut > 1 && end - cut < 4 && isCityWord(tokens, cut - 1, cut === end)) cut -= 1;
  // Trust the cut only if it stopped on something street-like; otherwise the
  // "city" might be the street name itself ("123 LAKE SHORE WI").
  return cut >= 2 && endsStreet(tokens, cut - 1) ? cut : end;
}

/**
 * Tokenize an owner query or parcel owner haystack. Names are order-independent
 * and we deliberately do NOT apply street-suffix folding (a "COURT" surname is
 * not a "CT"). "&" is folded to "AND" so both spellings match.
 */
export function normalizeOwnerTokens(text: string | null | undefined): string[] {
  if (!text) return [];
  return text
    .toUpperCase()
    .replace(/&/g, " AND ")
    .replace(/['`.]/g, "")
    .replace(/[^A-Z0-9 ]/g, " ")
    .split(/\s+/)
    .filter(Boolean);
}

/**
 * Surname particles that tax rolls write both joined and split: DEYOUNG and
 * DE YOUNG, MCDONALD and MC DONALD, VANDERBERG and VAN DER BERG. Measured over
 * the statewide owners (2026-10-08), most split surnames also appear joined
 * somewhere, and the joined form is usually the larger: MC 3,091 split rows
 * against 26,135 joined, DE 2,407 / 12,833, LA 1,655 / 9,396, VAN 5,206 /
 * 8,547. Typed one way, the other was invisible.
 */
export const OWNER_PARTICLES = ["VANDER", "VANDEN", "VAN", "VON", "DE", "DU", "DA", "DI", "LA", "LE", "MC", "MAC", "ST", "O"];
// Only ever after another particle: VAN DER, VAN DEN.
const INNER_PARTICLES = new Set([...OWNER_PARTICLES, "DER", "DEN"]);
const LEADING_PARTICLES = new Set(OWNER_PARTICLES);

/**
 * Owner haystack tokens for the client matcher: the name's words, plus each
 * particle joined to what follows it, so a typed "DeYoung" finds a roll that
 * writes "DE YOUNG". The other direction needs nothing: the matcher is a
 * substring match, so "DE" and "YOUNG" are both inside "DEYOUNG" already.
 */
export function ownerHaystackTokens(text: string | null | undefined): string[] {
  const tokens = normalizeOwnerTokens(text);
  const joined: string[] = [];
  for (let i = 0; i < tokens.length - 1; i++) {
    if (!LEADING_PARTICLES.has(tokens[i])) continue;
    let run = tokens[i];
    for (let j = i + 1; j < tokens.length; j++) {
      run += tokens[j];
      joined.push(run);
      if (!INNER_PARTICLES.has(tokens[j])) break;
    }
  }
  return joined.length ? [...tokens, ...joined] : tokens;
}

/**
 * Other spellings of one owner query token, for a matcher that cannot fold
 * the stored side (SQL `LIKE`). `tokens[i]` is always first.
 *
 * - After a typed particle run, the token joined to it: "De Young" also
 *   accepts DEYOUNG for YOUNG ("de" already prefix-matches DEYOUNG).
 * - A word that starts with a particle, split: "LaBonte" also accepts
 *   LA BONTE. The rest must be four letters or more to stay a prefix, or
 *   three as a whole word ("LeRoy" → LE ROY); shorter splits are not offered,
 *   since "Dean" as DE AN would find DE ANGELIS.
 *
 * Only alternatives are added, never a requirement, so nothing the plain
 * token matched stops matching.
 */
export function ownerTokenSpellings(tokens: string[], i: number): string[] {
  const token = tokens[i];
  const out = [token];
  // Joined to the particles typed before it.
  let run = token;
  for (let j = i - 1; j >= 0 && INNER_PARTICLES.has(tokens[j]); j--) {
    run = tokens[j] + run;
    if (LEADING_PARTICLES.has(tokens[j])) out.push(run);
  }
  // Split, when typed joined.
  if (!INNER_PARTICLES.has(token)) {
    for (const split of particleSplits(token, true)) out.push(split);
  }
  return Array.from(new Set(out));
}

function particleSplits(word: string, leading: boolean): string[] {
  const out: string[] = [];
  for (const particle of leading ? LEADING_PARTICLES : INNER_PARTICLES) {
    if (!word.startsWith(particle) || word.length === particle.length) continue;
    const rest = word.slice(particle.length);
    if (rest.length >= 4) out.push(`${particle} ${rest}`);
    else if (rest.length === 3) out.push(`${particle} ${rest} `);
    for (const deeper of particleSplits(rest, false)) out.push(`${particle} ${deeper}`);
  }
  return out;
}

/**
 * Every spelling that canonicalizes to the same token as `token` does.
 *
 * The client match folds both sides to one canonical form, but a SQL `LIKE`
 * has only the query side to work with, so it has to ask for each spelling
 * explicitly. Folding alone is not enough there: the abbreviation is a prefix
 * of the spelled-out word for STREET/ST and AVENUE/AVE, but not for ROAD/RD,
 * LANE/LN or COURT/CT, so a query folded to "RD" would silently miss every
 * row that stores "ROAD".
 *
 * Returns the token itself when it has no synonyms.
 */
export function expandAddressToken(token: string): string[] {
  const canon = SUFFIX_CANON[token] ?? DIRECTIONAL[token] ?? token;
  const spellings = ADDRESS_SYNONYMS.get(canon);
  return spellings ? [...spellings] : [token];
}

// canonical token -> every spelling that folds to it. Built once from the two
// synonym tables so adding a suffix there needs no second edit here.
const ADDRESS_SYNONYMS: Map<string, string[]> = (() => {
  const byCanon = new Map<string, string[]>();
  for (const table of [SUFFIX_CANON, DIRECTIONAL]) {
    for (const [spelling, canon] of Object.entries(table)) {
      const list = byCanon.get(canon);
      if (list) {
        if (!list.includes(spelling)) list.push(spelling);
      } else {
        byCanon.set(canon, [spelling]);
      }
    }
  }
  return byCanon;
})();

/** How many of the query tokens appear (as a substring) in the haystack tokens. */
export function countTokenMatches(hayTokens: string[], queryTokens: string[]): number {
  let matched = 0;
  for (const q of queryTokens) {
    if (hayTokens.some((h) => h.includes(q))) matched++;
  }
  return matched;
}

/** True when every query token is present in the haystack (strict AND match). */
export function matchesAllTokens(hayTokens: string[], queryTokens: string[]): boolean {
  return queryTokens.length > 0 && countTokenMatches(hayTokens, queryTokens) === queryTokens.length;
}

/**
 * A house or box number ("1100", "495") or a Wisconsin grid address ("N1234",
 * "W204N11509"). "1ST" and "42ND" are street names, not numbers.
 */
const NUMBERED_TOKEN = /^(\d+|[NSEW]\d+(?:[NSEW]\d+)?)$/i;

/**
 * The digit runs of an address query's numbered tokens, or `[]` when it has
 * none. Shared by the county map and the statewide worker, because it decides
 * the same thing in both: an address search also looks at the owner's
 * *mailing* address only when it names a specific numbered place. "15501
 * braun rd" and "po box 495" are worth finding in either column; a bare street
 * name is not ("main st" would add 23,920 owners who merely live on one).
 *
 * The digits double as a prefilter. Normalization never alters a run of
 * digits, so each one is a substring of any raw column a match came from;
 * letters are not safe that way ("OBRIEN" is not in "O'BRIEN").
 */
export function numberedAddressDigits(tokens: string[]): string[] {
  const digits: string[] = [];
  for (const token of tokens) {
    if (NUMBERED_TOKEN.test(token)) digits.push(...(token.match(/\d+/g) ?? []));
  }
  return digits;
}

/**
 * What to type into an address box to find every parcel whose owner gets mail
 * where this one's does — the "owner's other parcels" action.
 *
 * The ZIP and state go; the city stays. The address box drops an
 * envelope's city to match a *site* address, which is right there, but here
 * the city is what keeps "PO BOX 495 MILTON" from also finding every other
 * town's PO Box 495. Commas go too, or the first one would be read as the
 * start of a postal tail and take the city with it.
 */
export function mailingAddressSearchText(mail: string | null | undefined): string {
  let text = (mail ?? "")
    .replace(/,/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    // A care-of line names whoever handles the mail, and is often on only some
    // of an owner's parcels: "C/O FARMLAND OPPORTUNITY LLC PO BOX 2139" in one
    // county, "PO BOX 2139" in the next. The box is the part they share.
    .replace(/^(?:C\/O|ATTN:?|%)\s+.*?(?=\b(?:P\s?O\s+BOX|\d))/i, "");
  const withoutZip = text.replace(/\s+\d{5}(?:-?\d{4})?$/, "");
  // After a ZIP, the word before it is a state, Wisconsin or not. Without
  // one it may be a street suffix ("CT" is also Connecticut), so only WI goes.
  text = withoutZip !== text ? withoutZip.replace(/\s+(?:[A-Z]{2}|WISCONSIN)$/i, "") : text.replace(/\s+(?:WI|WISCONSIN)$/i, "");
  return text.trim();
}

/** Whether `mailingAddressSearchText` of this address would reach mailing addresses at all. */
export function canSearchMailingAddress(mail: string | null | undefined): boolean {
  const text = mailingAddressSearchText(mail);
  return text.length > 0 && numberedAddressDigits(normalizeAddressQuery(text)).length > 0;
}

/**
 * The ZIP at the end of a mailing address, after a state: "DELAVAN WI 53115",
 * "MERRILLAN, WI, 54754-8236", "WINONA MN 55987". Lowercase input. Requiring
 * the two-letter state (or "wisconsin") keeps a five-digit box or grid number
 * ("PO BOX 12345", "N12345") from reading as a ZIP. The same pattern runs in
 * SQL (RE2) on the other side of the match, so it uses nothing RE2 lacks.
 */
export const MAILING_ZIP_PATTERN = "(^|[^a-z])([a-z]{2}|wisconsin)[ ,.]+([0-9]{5})(-?[0-9]{4})? *$";
const MAILING_ZIP_RE = new RegExp(MAILING_ZIP_PATTERN);

/** The five-digit mailing ZIP, or null when the address carries none. */
export function mailingZip(mail: string | null | undefined): string | null {
  const match = (mail ?? "").toLowerCase().trim().match(MAILING_ZIP_RE);
  return match ? match[3] : null;
}

// Words that say nothing about who an owner is, so sharing one is no evidence
// that two parcels have the same owner.
const OWNER_FILLER = new Set([
  "AND", "THE", "OF", "LLC", "INC", "CORP", "CO", "LTD", "LLP", "LP", "TRUST",
  "TR", "TRS", "TRUSTEE", "TRUSTEES", "TRST", "REV", "REVOCABLE", "IRREVOCABLE",
  "LIVING", "FAMILY", "FAM", "ESTATE", "EST", "ETAL", "ETUX", "ETVIR", "ET",
  "AL", "UX", "JR", "SR", "II", "III", "IV", "CURRENT", "OWNER", "OWNERS",
  "LIFE", "AGREEMENT", "DATED", "DTD", "UNDER", "UTD", "UAD", "JOINT",
  "PROPERTY", "PROPERTIES", "HOLDINGS", "INVESTMENTS", "MANAGEMENT", "LAND",
  "FARM", "FARMS", "PARTNERSHIP", "COMPANY", "ASSOCIATION", "ASSN",
]);

/**
 * The words of an owner name that identify someone — surnames, given names,
 * the distinctive word of a company — for telling one owner's mailing address
 * from a stranger's that happens to read the same.
 */
export function distinctiveOwnerTokens(owner: string | null | undefined): string[] {
  return Array.from(
    new Set(
      normalizeOwnerTokens(owner).filter(
        (token) => token.length >= 3 && !/^\d+$/.test(token) && !OWNER_FILLER.has(token),
      ),
    ),
  );
}

/**
 * The street line of a mailing address, as canonical tokens: care-of, state
 * and ZIP dropped as `mailingAddressSearchText` drops them, and the city too
 * when a ZIP shows there was a postal tail. 32 counties publish only the
 * street line, so the street is the part every county's copy of one mailbox
 * shares; the ZIP, where there is one, is compared on its own.
 */
export function mailingStreetTokens(mail: string | null | undefined): string[] {
  const tokens = baseTokens(mailingAddressSearchText(mail));
  const end = mailingZip(mail) ? cutCity(tokens, tokens.length) : tokens.length;
  return normalizeAddressTokens(tokens.slice(0, end).join(" "));
}
